import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { fetchPlaces, fetchGuides } from "../api";

const PlacesContext = createContext(null);

export function PlacesProvider({ children }) {
  const [places, setPlaces] = useState([]);
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [favs, setFavs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pw_favs") || "[]"); } catch { return []; }
  });

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([fetchPlaces(), fetchGuides().catch(() => [])])
      .then(([p, g]) => { setPlaces(p); setGuides(g); setError(false); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { localStorage.setItem("pw_favs", JSON.stringify(favs)); }, [favs]);

  const getPlace = useCallback((slug) => places.find((p) => p.slug === slug), [places]);
  const placesFromSlugs = useCallback(
    (slugs) => slugs.map((s) => places.find((p) => p.slug === s)).filter(Boolean),
    [places]
  );
  const isFav = useCallback((slug) => favs.includes(slug), [favs]);
  const toggleFav = useCallback(
    (slug) => setFavs((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug])),
    []
  );

  const geo = useMemo(() => {
    const countries = [];
    const regionsByCountry = {};
    const citiesByCountry = {};
    const citiesByRegion = {};
    places.forEach((p) => {
      if (!countries.includes(p.country)) countries.push(p.country);
      (regionsByCountry[p.country] = regionsByCountry[p.country] || []);
      if (!regionsByCountry[p.country].includes(p.region)) regionsByCountry[p.country].push(p.region);
      (citiesByCountry[p.country] = citiesByCountry[p.country] || []);
      if (!citiesByCountry[p.country].includes(p.city)) citiesByCountry[p.country].push(p.city);
      const rk = `${p.country}|${p.region}`;
      (citiesByRegion[rk] = citiesByRegion[rk] || []);
      if (!citiesByRegion[rk].includes(p.city)) citiesByRegion[rk].push(p.city);
    });
    countries.sort();
    return { countries, regionsByCountry, citiesByCountry, citiesByRegion };
  }, [places]);

  const favPlaces = useMemo(() => favs.map((s) => places.find((p) => p.slug === s)).filter(Boolean), [favs, places]);

  const value = {
    places, guides, loading, error, reload: load,
    getPlace, placesFromSlugs, geo,
    favs, favPlaces, isFav, toggleFav,
  };
  return <PlacesContext.Provider value={value}>{children}</PlacesContext.Provider>;
}

export function usePlaces() {
  const ctx = useContext(PlacesContext);
  if (!ctx) throw new Error("usePlaces must be used within PlacesProvider");
  return ctx;
}
